import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";
import { forwardRef } from "react";

export const Textarea = forwardRef(function Textarea(
	{
		className,
		resizable = true,
		...props
	}: { className?: string; resizable?: boolean } & Omit<
		Headless.TextareaProps,
		"as" | "className"
	>,
	ref: React.ForwardedRef<HTMLTextAreaElement>,
) {
	return (
		<span
			data-slot="control"
			className={clsx([
				className,
				// Basic layout
				"relative block w-full",
				// Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
				"before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-base-100 before:shadow-sm",
				// Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
				"dark:before:hidden",
				// Focus ring
				"after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-primary",
				// Disabled state
				"has-data-disabled:opacity-50 has-data-disabled:before:bg-base-content/5 has-data-disabled:before:shadow-none",
			])}
		>
			<Headless.Textarea
				ref={ref}
				{...props}
				className={clsx([
					// Basic layout
					"relative block h-full w-full appearance-none rounded-lg px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
					// Typography
					"text-base/6 text-base-content placeholder:text-base-content/50 sm:text-sm/6",
					// Border
					"border border-base-content/10 data-hover:border-base-content/20",
					// Background color
					"bg-transparent dark:bg-base-content/5",
					// Hide default focus styles
					"focus:outline-hidden",
					// Invalid state
					"data-invalid:border-error data-invalid:data-hover:border-error",
					// Disabled state
					"disabled:border-base-content/20 dark:disabled:border-base-content/15 dark:disabled:bg-base-content/2.5 dark:data-hover:disabled:border-base-content/15",
					// Resizable
					resizable ? "resize-y" : "resize-none",
				])}
			/>
		</span>
	);
});
